import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import {
  fetchDashboardAlerts, fetchDashboardKpis, fetchWeeklyOrders, fetchWeeklyOrdersForYear,
  fetchHourlyOrders,
  fetchCategoryData, fetchDeliveryRecon, fetchFeedbackData, fetchVipData, feedbackExtras,
  fetchStateBreakdown, fetchDashboardSnapshot, fetchVipBoard, fetchAlertQueueCounts,
} from '../services/api';
import useIsMobile from '../hooks/useIsMobile';
import logoCp from '../logo-cp.svg';
import QuickCapture from '../components/QuickCapture';
import { getCurrentUser } from '../services/auth';
import FeedbackSheet, { sendYmd, sendLabel } from '../components/FeedbackSheet';
import VipBoardCard from '../components/VipBoardCard';
import OrderAlertsStrip from '../components/OrderAlertsStrip';

// A signed day-movement, e.g. "+20" / "−3" / "0". Module level because BOTH the phone
// hero tile and the desktop VIP ring print one, and a second copy is how the two boards
// worded the same number differently three times in five days.
// ⚠️ U+2212 MINUS, not a hyphen: it aligns with the digits at these weights.
const sgnDelta = (n) => (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(n).toLocaleString();

// All years share the same two brand colors; year label inside the bar differentiates them
const ORANGE = '#DC582A';
const NAVY   = '#00395C';
const getYearColor = (yr) => yr % 2 === 0 ? ORANGE : NAVY;

// Returns a Recharts label renderer that draws the year number inside the bar (mobile)
const makeBarLabel = (yr) => ({ x, y, width, height }) => {
  if (!height || height < 14) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} fill="rgba(255,255,255,0.82)"
      textAnchor="middle" dominantBaseline="middle" fontSize={8} fontWeight={700} fontFamily="Montserrat"
    >{yr}</text>
  );
};

// Draws the bar's value (order count) inside the bar (desktop)
const barValueLabel = ({ x, y, width, height, value }) => {
  if (!height || height < 14 || !value) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} fill="rgba(255,255,255,0.9)"
      textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={700} fontFamily="Montserrat"
    >{value}</text>
  );
};

// Custom tooltip for desktop YoY chart — shows date, orders, revenue, AOV
function YoyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const date = payload[0]?.payload?.date;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '8px 12px', fontSize: 11, fontFamily: 'Montserrat', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', minWidth: 180 }}>
      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>{date ? `${label} · ${date}` : label}</div>
      {payload.filter(p => p.value > 0).map((p, i) => {
        const yr = p.name;
        const rev = p.payload[`rev${yr}`] ?? '';
        const revNum = parseFloat(rev.replace(/[$,]/g, '')) || 0;
        const aov = p.value > 0 && revNum > 0 ? `$${(revNum / p.value).toFixed(2)}` : null;
        return (
          <div key={i} style={{ marginBottom: 3 }}>
            <span style={{ color: p.fill, fontWeight: 700 }}>{yr}: </span>
            <span style={{ color: 'var(--gray-700)' }}>{p.value} orders · {rev}{aov ? ` · AOV ${aov}` : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

const EXTRA_YEARS = [2024, 2023, 2022, 2021, 2020, 2019];

// Returns "Mar 8–14" or "Feb 28 – Mar 6" label for a given weeksBack
function getWeekLabel(weeksBack = 0) {
  const now = new Date();
  const dow = now.getDay();
  const daysToSat = dow === 6 ? 0 : 6 - dow;
  const sat = new Date(now); sat.setDate(now.getDate() + daysToSat - weeksBack * 7);
  const sun = new Date(sat); sun.setDate(sat.getDate() - 6);
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (sun.getMonth() === sat.getMonth())
    return `${M[sun.getMonth()]} ${sun.getDate()}–${sat.getDate()}`;
  return `${M[sun.getMonth()]} ${sun.getDate()} – ${M[sat.getMonth()]} ${sat.getDate()}`;
}

function getMapDateRange(period, year, weeksBack = 0) {
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${pad(d.getMonth()+1)}/${pad(d.getDate())}/${d.getFullYear()}`;
  const now = new Date();
  const offset = (2026 - year) * 364;
  if (period === 'today') {
    const d = new Date(now); d.setDate(d.getDate() - offset);
    const tomorrow = new Date(d); tomorrow.setDate(d.getDate() + 1);
    return { StartDate: fmt(d), EndDate: fmt(tomorrow) };
  }
  // 'week': match fetchWeeklyOrders — anchor on upcoming Saturday
  const dow = now.getDay();
  const daysToSat = dow === 6 ? 0 : 6 - dow;
  const sat = new Date(now); sat.setDate(now.getDate() + daysToSat - weeksBack * 7);
  const sun = new Date(sat); sun.setDate(sat.getDate() - 6);
  sun.setDate(sun.getDate() - offset);
  sat.setDate(sat.getDate() - offset);
  const endExclusive = new Date(sat); endExclusive.setDate(sat.getDate() + 1);
  return { StartDate: fmt(sun), EndDate: fmt(endExclusive) };
}

// ── State heat map constants ────────────────────────────────────────────────
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const NAME_TO_ABBR = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','District of Columbia':'DC',
  'Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL',
  'Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA',
  'Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN',
  'Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV',
  'New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY',
  'North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR',
  'Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',
  'Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA',
  'Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY',
};
const STATE_CENTROIDS = {
  AL:[-86.8,32.8],AK:[-153.4,64.2],AZ:[-111.7,34.3],AR:[-92.4,34.9],CA:[-119.6,37.2],
  CO:[-105.5,39.0],CT:[-72.7,41.6],DE:[-75.5,39.0],DC:[-77.0,38.9],FL:[-81.5,27.8],
  GA:[-83.4,32.7],HI:[-157.5,20.3],ID:[-114.5,44.4],IL:[-89.2,40.0],IN:[-86.3,40.0],
  IA:[-93.5,42.1],KS:[-98.4,38.5],KY:[-84.9,37.5],LA:[-91.8,31.2],ME:[-69.4,45.4],
  MD:[-76.6,39.0],MA:[-71.5,42.3],MI:[-85.4,44.3],MN:[-94.4,46.4],MS:[-89.7,32.7],
  MO:[-92.5,38.5],MT:[-109.6,47.0],NE:[-99.9,41.5],NV:[-116.4,39.3],NH:[-71.6,43.7],
  NJ:[-74.5,40.1],NM:[-106.2,34.5],NY:[-75.4,43.0],NC:[-79.4,35.6],ND:[-100.5,47.5],
  OH:[-82.8,40.4],OK:[-97.5,35.6],OR:[-120.5,44.0],PA:[-77.2,40.9],RI:[-71.5,41.7],
  SC:[-80.9,33.8],SD:[-100.0,44.4],TN:[-86.3,35.9],TX:[-99.3,31.5],UT:[-111.1,39.3],
  VT:[-72.7,44.0],VA:[-78.2,37.5],WA:[-120.5,47.4],WV:[-80.6,38.6],WI:[-89.6,44.5],
  WY:[-107.3,43.0],
};
const MAX_DOT_R = 26;
function heatColor(intensity) {
  if (!intensity) return '#e8eaed';
  const r = Math.round(0xf5 + (0xDC - 0xf5) * intensity);
  const g = Math.round(0xe8 + (0x58 - 0xe8) * intensity);
  const b = Math.round(0xd8 + (0x2A - 0xd8) * intensity);
  return `rgb(${r},${g},${b})`;
}

const alertPageMap = {
  'Orders Placed Today': 'placed-today',
  'Orders on Hold': 'on-hold',
  'Orders Needing Charge/Credit (Last 120 Days)': 'pending-charge',
  'Fraud Alerts': 'fraud-alert',
  'Product Alerts': 'product-alerts',
  'Physical Gift Certificate Orders': 'physical-gc',
  'VIP Club Packets': 'vip-club',
  'Order Shipping Errors': 'address-validations',
  'AK/HI Orders': 'ak-hi',
  'Shipping Address Validations': 'address-validations',
};

const ALERT_QUEUE_LABELS = { 'Fraud Alerts': true, 'Product Alerts': true };

function ApiError({ label, error }) {
  return (
    <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--red, #c0392b)', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, marginBottom: 8 }}>
      <strong>{label}:</strong> {error}
    </div>
  );
}

export default function Dashboard({ navigateTo }) {
  const [rawAlerts, setAlerts] = useState(null);
  // Fraud and Product Alerts counts come from the alert pages' own lists, not the
  // snapshot: the snapshot counts unprocessed orders with any score, which is a
  // different (and much larger) set than the queue the page opens.
  const [alertQueueCounts, setAlertQueueCounts] = useState(null);
  const alerts = rawAlerts && rawAlerts.map(a => {
    if (!(a.label in ALERT_QUEUE_LABELS)) return a;
    const n = alertQueueCounts ? alertQueueCounts[a.label] : undefined;
    if (n === undefined) return { ...a, value: '…', type: 'zero' };
    if (n === null) return a;
    return { ...a, value: n, type: n === 0 ? 'zero' : n > 500 ? 'danger' : 'warning' };
  });
  const [alertsError, setAlertsError] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [kpiError, setKpiError] = useState(null);
  const [weeklyOrdersData, setWeeklyOrdersData] = useState(null);
  const [weeklyError, setWeeklyError] = useState(null);
  const [selectedYears, setSelectedYears] = useState(new Set());
  const [extraYearData, setExtraYearData] = useState({});
  const [loadingYears, setLoadingYears] = useState(new Set());
  const [categoryData, setCategoryData] = useState(null);
  const [categoryError, setCategoryError] = useState(null);
  const [deliveryRecon, setDeliveryRecon] = useState(null);
  const [reconError, setReconError] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  // The Feedback tile's pop-up (null = closed) and the counts under its scores (photo
  // entries, customers sent to review sites), both for the send the tile shows.
  const [feedbackSheet, setFeedbackSheet] = useState(null);
  const [feedbackCounts, setFeedbackCounts] = useState(null);
  const feedbackYmd = sendYmd(feedbackData);
  const feedbackSheetOpen = feedbackSheet !== null;
  useEffect(() => {
    if (!feedbackYmd) return;
    let live = true;
    const load = () => feedbackExtras(feedbackYmd).then((x) => { if (live) setFeedbackCounts(x); }).catch(() => { if (live) setFeedbackCounts(null); });
    load();
    // Keep the counts current while the dashboard sits open, and re-read them when the
    // pop-up closes, so the tile never trails the list it opens (Greg, Oct 21, 2026).
    const t = setInterval(load, 5 * 60e3);
    return () => { live = false; clearInterval(t); };
  }, [feedbackYmd, feedbackSheetOpen]);
  // How many review requests went out in this send, for the response rate beside the
  // count (Greg, Oct 26, 2026; the wall board already shows it). Its own call: a cold
  // lookup asks the live admin and can take a few seconds.
  const [feedbackRequests, setFeedbackRequests] = useState(null);
  useEffect(() => {
    if (!feedbackYmd) return;
    let live = true;
    feedbackExtras(feedbackYmd, { requests: 1 })
      .then((x) => { if (live) setFeedbackRequests(Number(x && x.requests_sent) || null); })
      .catch(() => { if (live) setFeedbackRequests(null); });
    return () => { live = false; };
  }, [feedbackYmd]);
  const feedbackRate = feedbackData && feedbackRequests
    ? Math.round(Number(feedbackData.count || 0) / feedbackRequests * 100) : null;
  const [vipData, setVipData] = useState(null);
  const [vipError, setVipError] = useState(null);
  const [vipPurchasesOpen, setVipPurchasesOpen] = useState(false);
  // The TV board's VIP payload, for the desktop card (kiosk-dashboard, session-auth).
  const [vipBoard, setVipBoard] = useState(null);
  const [vipBoardError, setVipBoardError] = useState(null);
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Error, setGa4Error] = useState(null);
  const [ga4DrillMetric, setGa4DrillMetric] = useState(null); // null | 'sessions' | 'activeUsers' | 'transactions' | 'purchaseRevenue'
  const [ga4DrillCache, setGa4DrillCache] = useState({}); // keyed by `${view}:${metric}`
  const [ga4DrillLoading, setGa4DrillLoading] = useState(false);
  const [ga4DrillError, setGa4DrillError] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  const isMobile = useIsMobile();

  // Mobile YoY chart state
  const [yoyView, setYoyView] = useState('today'); // 'today' | 'week' | 'map'
  const [mobileWeeksBack, setMobileWeeksBack] = useState(0);
  const [mobileWeekData, setMobileWeekData] = useState(null);
  const [mobileWeekLoading, setMobileWeekLoading] = useState(false);
  const [todayMode, setTodayMode] = useState('day'); // 'day' | 'hour'
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);

  // Heat map state (shared mobile + desktop)
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showDots, setShowDots] = useState(true);
  const [mapScale, setMapScale] = useState(90);
  const [mapTooltip, setMapTooltip] = useState(null);
  const [desktopShowMap, setDesktopShowMap] = useState(false);
  const [desktopYoyPeriod, setDesktopYoyPeriod] = useState('week'); // 'week' | 'today'
  const [desktopWeeksBack, setDesktopWeeksBack] = useState(0);
  const [desktopHistData, setDesktopHistData] = useState(null);
  const [desktopHistLoading, setDesktopHistLoading] = useState(false);
  const [mapYear, setMapYear] = useState(2026);
  const [mobileMapPeriod, setMobileMapPeriod] = useState('today'); // last non-map view (matches yoyView default)

  useEffect(() => {
    const active = desktopShowMap || yoyView === 'map';
    if (!active) return;
    const period = desktopShowMap ? desktopYoyPeriod : mobileMapPeriod;
    const wb = desktopShowMap ? (desktopYoyPeriod === 'week' ? desktopWeeksBack : 0) : (mobileMapPeriod === 'week' ? mobileWeeksBack : 0);
    setMapData(null);
    setMapLoading(true);
    fetchStateBreakdown(getMapDateRange(period, mapYear, wb))
      .then(setMapData)
      .catch(() => {})
      .finally(() => setMapLoading(false));
  }, [desktopShowMap, yoyView, mapYear, mobileMapPeriod, desktopYoyPeriod, desktopWeeksBack, mobileWeeksBack]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (desktopWeeksBack === 0) { setDesktopHistData(null); return; }
    setDesktopHistLoading(true);
    fetchWeeklyOrders(desktopWeeksBack)
      .then(setDesktopHistData)
      .catch(() => {})
      .finally(() => setDesktopHistLoading(false));
  }, [desktopWeeksBack]);

  useEffect(() => {
    if (!isMobile || yoyView !== 'week' || (mobileWeeksBack === 0 && weeklyOrdersData)) {
      setMobileWeekData(null);
      return;
    }
    setMobileWeekLoading(true);
    fetchWeeklyOrders(mobileWeeksBack)
      .then(setMobileWeekData)
      .catch(() => {})
      .finally(() => setMobileWeekLoading(false));
  }, [isMobile, yoyView, mobileWeeksBack]);

  useEffect(() => {
    if (!isMobile || yoyView !== 'today' || todayMode !== 'hour') return;
    if (hourlyData) return;
    setHourlyLoading(true);
    fetchHourlyOrders()
      .then(setHourlyData)
      .catch(() => {})
      .finally(() => setHourlyLoading(false));
  }, [isMobile, yoyView, todayMode, hourlyData]);

  // GA4 YoY metrics — refetches when toggling Today / Week. Map view ignored.
  useEffect(() => {
    if (!isMobile) return;
    if (yoyView !== 'today' && yoyView !== 'week') return;
    setGa4Data(null);
    setGa4Error(null);
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions'
      : '/.netlify/functions';
    fetch(`${base}/ga4-yoy?view=${yoyView}`)
      .then(r => r.json().then(j => ({ ok: r.ok, status: r.status, body: j })))
      .then(({ ok, status, body }) => {
        if (!ok) {
          setGa4Error(status === 503 ? 'GA4 not configured' : (body.error || `GA4 error (${status})`));
          return;
        }
        setGa4Data(body);
      })
      .catch(e => setGa4Error(e.message));
  }, [isMobile, yoyView]);

  // GA4 tile drill-down: fetch per-channel breakdown when a tile is tapped.
  // Cached by `${view}:${metric}` so re-tapping the same tile is instant.
  useEffect(() => {
    if (!ga4DrillMetric) return;
    const cacheKey = `${yoyView}:${ga4DrillMetric}`;
    if (ga4DrillCache[cacheKey]) return;
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions'
      : '/.netlify/functions';
    setGa4DrillLoading(true);
    setGa4DrillError(null);
    fetch(`${base}/ga4-yoy-by-channel?view=${yoyView}&metric=${ga4DrillMetric}`)
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (!ok) { setGa4DrillError(body.error || 'GA4 drill-down failed'); return; }
        setGa4DrillCache(prev => ({ ...prev, [cacheKey]: body }));
      })
      .catch(e => setGa4DrillError(e.message))
      .finally(() => setGa4DrillLoading(false));
  }, [ga4DrillMetric, yoyView, ga4DrillCache]);

  useEffect(() => {
    // KPIs always live (one-off api-bak fetch, never snapshotted).
    fetchDashboardKpis().then(setKpiData).catch(e => setKpiError(e.message));
    fetchAlertQueueCounts().then(setAlertQueueCounts).catch(() => setAlertQueueCounts({}));

    // VIP signup counts (today + this week) — live, not snapshotted; tiny query.

    // Everything else: try the precomputed snapshot first (single Neon SELECT,
    // up to 15 min stale, refreshed by dashboard-snapshot-refresh.js). Fall
    // back to live per-section fetches if snapshot is missing or stale-load fails.
    const liveFetches = () => {
      fetchDashboardAlerts().then(setAlerts).catch(e => setAlertsError(e.message));
      fetchWeeklyOrders().then(setWeeklyOrdersData).catch(e => setWeeklyError(e.message));
      fetchDeliveryRecon().then(setDeliveryRecon).catch(e => setReconError(e.message));
      fetchFeedbackData().then(setFeedbackData).catch(e => setFeedbackError(e.message));
      fetchVipData().then(setVipData).catch(e => setVipError(e.message));
    };

    // Per-section shape sentinels — if a snapshot field is present but
    // doesn't carry the keys the render path needs, treat it as missing and
    // fall back to a live fetch instead. Defends against builder/consumer
    // drift (e.g. nested vs. flat vipData shape that blanked the dashboard
    // for every authenticated user once).
    const vipShapeOk = (v) => v && v.week && v.month && 'newVips' in v.week;

    fetchDashboardSnapshot().then(snap => {
      if (!snap) { liveFetches(); return; }
      if (snap.alerts)         setAlerts(snap.alerts);
      if (snap.weeklyOrders)   setWeeklyOrdersData(snap.weeklyOrders);
      if (snap.deliveryRecon)  setDeliveryRecon(snap.deliveryRecon);
      if (snap.feedbackData)   setFeedbackData(snap.feedbackData);
      if (vipShapeOk(snap.vipData)) setVipData(snap.vipData);
      // If any section was null in the snapshot (refresh partial-failed), backfill it live.
      if (!snap.alerts)        fetchDashboardAlerts().then(setAlerts).catch(e => setAlertsError(e.message));
      if (!snap.weeklyOrders)  fetchWeeklyOrders().then(setWeeklyOrdersData).catch(e => setWeeklyError(e.message));
      if (!snap.deliveryRecon) fetchDeliveryRecon().then(setDeliveryRecon).catch(e => setReconError(e.message));
      if (!snap.feedbackData)  fetchFeedbackData().then(setFeedbackData).catch(e => setFeedbackError(e.message));
      if (!vipShapeOk(snap.vipData)) fetchVipData().then(setVipData).catch(e => setVipError(e.message));
    }).catch(() => liveFetches());

    // ⚠️ NOT desktop-only, though it was. The mobile hero tile used to compute its own
    // signup figures from vip_signups_periods; on 2026-09-02 that was retired in favour of
    // the board payload, which is the only place the corrected numbers live - and this
    // fetch was still behind `if (!isMobile)`, so the phone silently lost the line
    // altogether. Greg: "I feel like I had the plus number for VIPs at some point."
    // He did. One aggregate-only request, and it feeds both layouts.
    fetchVipBoard().then(setVipBoard).catch(e => setVipBoardError(e.message));

    // Category sales chart is desktop-only and not in the snapshot — keep live.
    if (!isMobile) {
      fetchCategoryData().then(setCategoryData).catch(e => setCategoryError(e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⚠️ COMPUTED ONCE, FOR BOTH LAYOUTS, and it lives out here for exactly that reason.
  // Greg, 2026-09-02: "it's the same function as for the pwa. i just want it to display
  // in desktop." It used to sit inside the isMobile branch, so the desktop could not
  // reach it without a second copy - and a second copy of this particular number is how
  // the phone and the wall came to print +15, +14 and +13 for the same day.
  //
  //   +15  every VIP PURCHASE, which counts a current member renewing early
  //   +14  people who actually STARTED a membership today
  //   +13  the net change in RENEWING members, which is what the big number moves by
  //
  // ⚠️ CORRECTED 2026-09-17: this was the NET move in the renewing pool, and the wall
  // board's chip is today's SIGNUPS - so the phone read "6,137 +28" while the TV six
  // feet away read "6,136 +29" for the same minute, and the line underneath both of
  // them said "29 signed up today". Greg: "the PWA is different than the fully kiosk
  // dashboard... figure out which one is right, and make them both the same."
  //
  // Signups is the one Greg settled on (see reference_vip_purchase_is_not_a_signup):
  // every membership purchase plus team flips, an ex-VIP coming back included. The NET
  // still exists and is still shown - it is what the 7-day bars are - but it does not
  // belong in the one loud slot, because signups and the renewing-pool delta answer
  // different questions and must NOT be forced to reconcile. A duplicate sale (VIP-62)
  // is a signup that cannot move the pool, which is exactly why they differ by one today.
  const vipSignedToday = Number(vipBoard?.signed_up_today || 0);
  // Empty string on a quiet day, so callers render nothing rather than a zero - a 0 in
  // the loud slot beside a live count reads as a fault.
  const vipSignupCount = vipSignedToday > 0 ? sgnDelta(vipSignedToday) : '';
  // The NET change in renewing members, composed by the server and printed verbatim, for
  // the UNLABELLED chip on the phone hero tile. Greg, 2026-09-20: "I want the plus to be
  // reflective of the net for the day." It is the same string the wall prints and the same
  // value as the last bar of the 7-day chart, so those two can no longer disagree.
  // ⚠️ vipSignupCount above is NOT retired - the desktop line under the ring says
  // "N signed up today" in words, which is a different and correctly-labelled figure.
  // Only the chips that carry no label had to change.
  const vipTodayChip = vipBoard?.today_chip || '';
  // The renewing / active counts the wall prints, from the SAME payload. The snapshot
  // fallback is production's own dashboard figure (vip_dashboard_snapshot, written by
  // sync-incremental from GetDashboardVIPClubInfo) and it runs about one member ahead
  // of our mirror - fine as a fallback, wrong as the headline over mirror-derived
  // movement, which is what it was doing here.
  const vipRenewingCount = vipBoard && vipBoard.renewals != null
    ? Number(vipBoard.renewals) : (vipData ? vipData.activeRenewals : null);
  const vipActiveCount = vipBoard && vipBoard.active != null
    ? Number(vipBoard.active) : (vipData ? vipData.activeMembers : null);

  if (isMobile) {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const todayDayIdx = new Date().getDay();
    const todayEntry  = weeklyOrdersData?.[todayDayIdx];
    const placedToday   = alerts?.find(a => a.label === 'Orders Placed Today');
    const shippingToday = alerts?.find(a => a.label === 'Orders Shipping Today');

    // Week label helper for navigation
    const weekLabel = (wb) => {
      const ref = new Date();
      const dow = ref.getDay();
      const toSat = (dow === 6 ? 0 : dow + 1) + wb * 7;
      const sat = new Date(ref.getTime() - toSat * 86400000);
      const sun = new Date(sat.getTime() - 6 * 86400000);
      const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${fmt(sun)} – ${fmt(sat)}`;
    };

    const yoyChartData = mobileWeeksBack === 0 ? weeklyOrdersData : mobileWeekData;
    const navBtn = { background: 'var(--gray-200)', border: 'none', borderRadius: 8, width: 34, height: 34, fontSize: 18, fontWeight: 700, cursor: 'pointer', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const keyAlerts = alerts ? [
      alerts.find(a => a.label === 'Late Delivery'),
      alerts.find(a => a.label === 'Orders on Hold'),
      alerts.find(a => a.label === 'Fraud Alerts'),
      alerts.find(a => a.label === 'Orders Needing Charge/Credit (Last 120 Days)'),
      alerts.find(a => a.label === 'Open Claim Orders (Last 120 Days)'),
      alerts.find(a => a.label === 'Orders Remaining Today'),
    ].filter(Boolean) : [];
    // "N checkout · N CS wins" under the new-VIP count, or null when there is nothing
    // worth the line. A customer who was emailed the offer AND then bought at checkout
    // is counted once, credited to CS (Greg's rule) — the dedupe happens server-side.
    // ⚠️ BOTH FIGURES COME FROM THE BOARD PAYLOAD (kiosk-dashboard), THE SAME PLACE THE
    // WALL READS. This tile used to compute its own from `vip_signups_periods`, and on
    // 2026-09-01 the phone said "+15 today · 9 checkout · 6 CS wins" while the wall six feet
    // away said "+13 today · 14 signed up today · 8 checkout · 6 added by the team". Three
    // different numbers for one day, because they were three different questions:
    //
    //   +15  every VIP PURCHASE, which counts a current member renewing early
    //   +14  people who actually STARTED a membership today
    //   +13  the net change in renewing members, which is what the big number above moves by
    //
    // The chip belongs to the number it sits under, so it is the net; the split explains it.
    // Nothing here recomputes either one.
    const vipRange = vipBoard?.signups_range || null;
    const vipSignupSplit = (() => {
      if (yoyView === 'week') {
        if (!vipRange || !(vipRange.total > 0)) return null;
        const team = vipRange.added_by_team;
        return `${vipRange.checkout} checkout${team != null ? ` · ${team} added by the team` : ''}`;
      }
      // Composed server-side so the phone and the wall cannot word it differently.
      return vipBoard?.day_split || null;
    })();
    const overallFeedback = feedbackData?.scores?.find(s => s.label === 'Overall');
    const vipPct = vipData ? Math.round(vipData.current / vipData.goal2026 * 100) : 0;

    return (
      <div style={{ paddingBottom: 24 }}>

        {/* ── MOBILE HEADER ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12 }}>
          <a onClick={() => navigateTo('dashboard')} style={{ cursor: 'pointer', fontSize: 12, color: 'var(--orange)', fontWeight: 600 }}>↻ Refresh</a>
        </div>

        <OrderAlertsStrip navigateTo={navigateTo} />

        {/* ── TODAY HERO ── */}
        <div className="hero-glass">
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>{today}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Today's Activity</div>
            {/* Capture lands in Greg's own inbox, so it is his button only. */}
            {getCurrentUser()?.staffName === 'Greg Cain' && <QuickCapture />}
          </div>
          {alertsError && <div style={{ fontSize: 11, color: '#ffb3a0' }}>⚠ {alertsError}</div>}
          {!alertsError && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* VIP tile — Renewing VIPs (big) + Active VIPs + signups (today or this-week, follows yoyView) */}
              <div className="hero-tile">
                <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Renewing VIPs
                </div>
                {vipData ? (
                  <>
                    {/* The day's movement sits ON the number it moves, big enough to read at
                        arm's length, and unlabelled: "today" is the only thing it could mean
                        beside a live count, and the words were costing more room than they
                        were worth on a phone. Greg, 2026-09-02. */}
                    <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>
                      {(vipRenewingCount ?? vipData.activeRenewals).toLocaleString()}
                      {vipTodayChip && (
                        <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--peach)', marginLeft: 10 }}>
                          {vipTodayChip.replace(' today', '')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>
                      {(vipActiveCount ?? vipData.activeMembers).toLocaleString()} active VIPs
                    </div>
                    {/* What moved the number above, in the wall board's own words. Blank
                        rather than approximate if the board payload has not landed: a
                        different number here than the wall shows is the whole bug this
                        replaced. */}
                    {vipSignupSplit && (
                      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{vipSignupSplit}</div>
                    )}
                    {/* A membership sold to somebody who already had a live one. Composed
                        server-side and printed verbatim, on its OWN line, because it is a
                        site defect rather than a term in the day's sum. The wall carries
                        it; the phone must not quietly drop it. */}
                    {vipBoard?.duplicate_note && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--peach)', marginTop: 2 }}>{vipBoard.duplicate_note}</div>
                    )}
                  </>
                ) : (
                  <>
                    <div><span className="skel skel-light" style={{ width: 64, height: 32 }} /></div>
                    <div style={{ marginTop: 8 }}><span className="skel skel-light" style={{ width: 80, height: 13 }} /></div>
                  </>
                )}
              </div>

              {/* Feedback tile (Greg, Oct 20, 2026): the live admin's Feedback card in the hero,
                  replacing Shipping Today, which moved down the page. The whole tile opens the
                  pop-up (scores, Product by category, photos, review sites); the two counts at
                  the bottom jump straight to their lists. */}
              <div className="hero-tile" role="button" tabIndex={0}
                onClick={() => feedbackData && setFeedbackSheet('scores')}
                onKeyDown={(e) => { if (feedbackData && (e.key === 'Enter' || e.key === ' ')) setFeedbackSheet('scores'); }}
                style={{ cursor: feedbackData ? 'pointer' : 'default' }}>
                <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Feedback
                </div>
                {feedbackError && !feedbackData && <div style={{ fontSize: 11, color: '#ffb3a0' }}>⚠ {feedbackError}</div>}
                {feedbackData ? (
                  <>
                    <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>
                      {Number(feedbackData.count || 0).toLocaleString()}
                      {overallFeedback && (
                        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--peach)', marginLeft: 10 }}>★ {overallFeedback.score}</span>
                      )}
                    </div>
                    {feedbackRate != null && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--peach)', marginTop: 6 }}>
                        {feedbackRate}% engagement <span style={{ fontWeight: 400, opacity: 0.85 }}>· {Number(feedbackData.count || 0).toLocaleString()} of {feedbackRequests.toLocaleString()}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>
                      Product {feedbackData.scores?.find(s => s.label === 'Product')?.score ?? '–'}
                      {feedbackYmd ? ` · sent ${sendLabel(feedbackYmd)}` : ''}
                    </div>
                    {feedbackCounts && (
                      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, lineHeight: 1.6 }}>
                        <span onClick={(e) => { e.stopPropagation(); setFeedbackSheet('photos'); }} style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>
                          {feedbackCounts.photos} photo {feedbackCounts.photos === 1 ? 'entry' : 'entries'}
                        </span>
                        {' · '}
                        <span onClick={(e) => { e.stopPropagation(); setFeedbackSheet('reviews'); }} style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>
                          {feedbackCounts.review_sites} sent to review sites
                        </span>
                        {feedbackCounts.review_sites_avg != null && ` (★ ${feedbackCounts.review_sites_avg.toFixed(2)} avg)`}
                      </div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--peach)', marginTop: 6 }}>See Scores ›</div>
                  </>
                ) : !feedbackError && (
                  <>
                    <div><span className="skel skel-light" style={{ width: 64, height: 32 }} /></div>
                    <div style={{ marginTop: 8 }}><span className="skel skel-light" style={{ width: 80, height: 11 }} /></div>
                    <div style={{ marginTop: 4 }}><span className="skel skel-light" style={{ width: 100, height: 10 }} /></div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── YOY ORDERS ── */}
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Year-Over-Year Orders</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['today','Today'],['week','Week'],['map','🗺 Map']].map(([v, label]) => (
                <button key={v} onClick={() => { if (v !== 'map') setMobileMapPeriod(v); setYoyView(v); }} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: yoyView === v ? 'var(--orange)' : 'var(--gray-200)', color: yoyView === v ? '#fff' : 'var(--gray-600)' }}>
                  {label}
                </button>
              ))}
              {yoyView === 'map' && (
                <>
                  {[2026, 2025].map(yr => (
                    <button key={yr} onClick={() => setMapYear(yr)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: mapYear === yr ? getYearColor(yr) : 'var(--gray-200)', color: mapYear === yr ? '#fff' : 'var(--gray-600)' }}>{yr}</button>
                  ))}
                  <button onClick={() => setShowDots(d => !d)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: showDots ? 'var(--navy)' : 'var(--gray-200)', color: showDots ? '#fff' : 'var(--gray-600)' }}>⬤ Dots</button>
                  <button onClick={() => setMapScale(s => Math.max(40, s - 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>−</button>
                  <button onClick={() => setMapScale(s => Math.min(150, s + 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>+</button>
                </>
              )}
            </div>
          </div>

          {/* TODAY VIEW */}
          {yoyView === 'today' && (
            <div>
              {(!weeklyOrdersData || !placedToday) && !weeklyError && (
                <div style={{ padding: '16px' }}>
                  <div style={{ textAlign: 'center', marginBottom: 14 }}>
                    <span className="skel" style={{ width: 200, height: 12 }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[0, 1].map(i => (
                      <div key={i} style={{ textAlign: 'center', padding: '4px' }}>
                        <div style={{ marginBottom: 6 }}><span className="skel" style={{ width: 36, height: 10 }} /></div>
                        <div><span className="skel" style={{ width: 64, height: 44 }} /></div>
                        <div style={{ marginTop: 6 }}><span className="skel" style={{ width: 90, height: 14 }} /></div>
                        <div style={{ marginTop: 4 }}><span className="skel" style={{ width: 110, height: 11 }} /></div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 14 }}><span className="skel" style={{ width: '100%', height: 140, borderRadius: 6 }} /></div>
                </div>
              )}
              {weeklyError && <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--red)' }}>⚠ {weeklyError}</div>}
              {placedToday && (() => {
                const todayOrders2026 = placedToday.value;
                const todayRev2026    = placedToday.sub?.[0]?.value ?? '';
                const aov2026         = placedToday.sub?.[1]?.value ?? '';
                const last2025        = todayEntry?.orders2025 ?? 0;
                const rev2025Raw      = parseFloat((todayEntry?.rev2025 ?? '').replace(/[$,]/g, '')) || 0;
                const rev2026Raw      = parseFloat((todayRev2026 ?? '').toString().replace(/[$,]/g, '')) || 0;
                const aov2025         = last2025 > 0 ? `$${(rev2025Raw / last2025).toFixed(2)}` : null;
                const ordersDiff = todayOrders2026 - last2025;
                const ordersPct  = last2025 > 0 ? (ordersDiff / last2025 * 100) : null;
                const revDiff    = rev2026Raw - rev2025Raw;
                const revPct     = rev2025Raw > 0 ? (revDiff / rev2025Raw * 100) : null;
                const todayDate = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                const deltaColor = (d) => d > 0 ? 'var(--green)' : d < 0 ? 'var(--red)' : 'var(--gray-900)';
                const deltaCell  = (d) => ({ fontSize: 16, fontWeight: 700, color: deltaColor(d), textAlign: 'center', whiteSpace: 'nowrap' });
                const deltaText  = (d, p) => p === null ? '' : `${d > 0 ? '▲ ' : d < 0 ? '▼ ' : ''}${Math.abs(p).toFixed(1)}%`;
                return (
                  <>
                    <div style={{ padding: '16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 14, textAlign: 'center' }}>
                        {todayDate}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', columnGap: 12, rowGap: 6, alignItems: 'center' }}>
                        {/* Year labels (2026 left, 2025 right) */}
                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center' }}>2026</div>
                        <div></div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center' }}>2025</div>

                        {/* Orders row */}
                        <div style={{ fontSize: 44, fontWeight: 800, color: 'var(--orange)', lineHeight: 1, textAlign: 'center' }}>
                          {todayOrders2026}
                        </div>
                        <div style={deltaCell(ordersDiff)}>
                          {todayEntry ? deltaText(ordersDiff, ordersPct) : ''}
                        </div>
                        <div style={{ fontSize: 44, fontWeight: 800, color: 'var(--navy)', lineHeight: 1, textAlign: 'center' }}>
                          {todayEntry ? last2025 : '—'}
                        </div>

                        {/* Revenue row */}
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--orange)', textAlign: 'center' }}>
                          {todayRev2026}
                        </div>
                        <div style={deltaCell(revDiff)}>
                          {todayEntry ? deltaText(revDiff, revPct) : ''}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>
                          {todayEntry?.rev2025 ?? ''}
                        </div>

                        {/* AOV row */}
                        {(aov2025 || aov2026) && (
                          <>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
                              {aov2026 && <>AOV: <strong style={{ color: 'var(--orange)' }}>{aov2026}</strong></>}
                            </div>
                            <div></div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
                              {aov2025 && <>AOV: <strong style={{ color: 'var(--navy)' }}>{aov2025}</strong></>}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Day / Hour sub-toggle */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '0 16px 4px' }}>
                      {[['day','Day'],['hour','Hour']].map(([v, label]) => (
                        <button key={v} onClick={() => setTodayMode(v)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: todayMode === v ? 'var(--navy)' : 'var(--gray-200)', color: todayMode === v ? '#fff' : 'var(--gray-600)' }}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* Single-day bar chart */}
                    {todayMode === 'day' && todayEntry && (
                      <div style={{ padding: '4px 4px 4px' }}>
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={[{ day: new Date().toLocaleDateString('en-US', { weekday: 'short' }), orders2026: todayOrders2026, orders2025: todayEntry.orders2025 }]} barGap={8} barSize={48}>
                            <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} width={28} />
                            <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'Montserrat' }} />
                            <Bar dataKey="orders2026" fill={ORANGE} name="2026" radius={[2,2,0,0]} />
                            <Bar dataKey="orders2025" fill={NAVY}   name="2025" radius={[2,2,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Hourly bar chart — today vs same weekday 364d ago */}
                    {todayMode === 'hour' && (
                      <div style={{ padding: '4px 4px 4px' }}>
                        {hourlyLoading && !hourlyData && (
                          <div style={{ padding: '20px 16px' }}><span className="skel" style={{ width: '100%', height: 140, borderRadius: 6 }} /></div>
                        )}
                        {hourlyData && (() => {
                          const fmtHour = (h) => h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`;
                          const chartData = hourlyData.map(r => ({ ...r, label: fmtHour(r.hour) }));
                          return (
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart data={chartData} barGap={1} barSize={4} barCategoryGap={4}>
                                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={2} />
                                <YAxis tick={{ fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'Montserrat' }} labelFormatter={(l) => l} />
                                <Bar dataKey="orders2026" fill={ORANGE} name="2026" radius={[2,2,0,0]} />
                                <Bar dataKey="orders2025" fill={NAVY}   name="2025" radius={[2,2,0,0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* WEEK VIEW */}
          {yoyView === 'week' && (
            <>
              {/* Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 0' }}>
                <button style={navBtn} onClick={() => setMobileWeeksBack(w => w + 1)}>‹</button>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{weekLabel(mobileWeeksBack)}</span>
                <button style={{ ...navBtn, opacity: mobileWeeksBack <= -4 ? 0.3 : 1 }} disabled={mobileWeeksBack <= -4} onClick={() => setMobileWeeksBack(w => Math.max(-4, w - 1))}>›</button>
              </div>

              {/* Loading */}
              {mobileWeekLoading && (
                <div style={{ padding: '12px 16px 0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    {[0, 1].map(i => (
                      <div key={i} style={{ textAlign: 'center', padding: '4px' }}>
                        <div style={{ marginBottom: 6 }}><span className="skel" style={{ width: 36, height: 10 }} /></div>
                        <div><span className="skel" style={{ width: 70, height: 36 }} /></div>
                        <div style={{ marginTop: 6 }}><span className="skel" style={{ width: 90, height: 14 }} /></div>
                        <div style={{ marginTop: 4 }}><span className="skel" style={{ width: 110, height: 11 }} /></div>
                      </div>
                    ))}
                  </div>
                  <div><span className="skel" style={{ width: '100%', height: 160, borderRadius: 6 }} /></div>
                </div>
              )}

              {/* Summary + chart */}
              {!mobileWeekLoading && yoyChartData && (() => {
                const sumRev = (key) => yoyChartData.reduce((s, d) => s + (parseFloat((d[key] || '').toString().replace(/[$,]/g, '')) || 0), 0);
                const sumOrd = (key) => yoyChartData.reduce((s, d) => s + (Number(d[key]) || 0), 0);
                const ord25 = sumOrd('orders2025');
                const ord26 = sumOrd('orders2026');
                const rev25 = sumRev('rev2025');
                const rev26 = sumRev('rev2026');
                const aov25 = ord25 > 0 ? rev25 / ord25 : 0;
                const aov26 = ord26 > 0 ? rev26 / ord26 : 0;
                const fmt$ = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const ordersDiff = ord26 - ord25;
                const ordersPct = ord25 > 0 ? (ordersDiff / ord25 * 100) : null;
                const revDiff = rev26 - rev25;
                const revPct = rev25 > 0 ? (revDiff / rev25 * 100) : null;
                const deltaColor = (d) => d > 0 ? 'var(--green)' : d < 0 ? 'var(--red)' : 'var(--gray-900)';
                const deltaCell  = (d) => ({ fontSize: 16, fontWeight: 700, color: deltaColor(d), textAlign: 'center', whiteSpace: 'nowrap' });
                const deltaText  = (d, p) => p === null ? '' : `${d > 0 ? '▲ ' : d < 0 ? '▼ ' : ''}${Math.abs(p).toFixed(1)}%`;
                return (
                  <>
                    {/* 2-pane summary */}
                    <div style={{ padding: '12px 16px 0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', columnGap: 12, rowGap: 6, alignItems: 'center' }}>
                        {/* Year labels (2026 left, 2025 right) */}
                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center' }}>2026</div>
                        <div></div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.6px', textAlign: 'center' }}>2025</div>

                        {/* Orders row */}
                        <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--orange)', lineHeight: 1, textAlign: 'center' }}>{ord26.toLocaleString()}</div>
                        <div style={deltaCell(ordersDiff)}>
                          {deltaText(ordersDiff, ordersPct)}
                        </div>
                        <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--navy)', lineHeight: 1, textAlign: 'center' }}>{ord25.toLocaleString()}</div>

                        {/* Revenue row */}
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)', textAlign: 'center' }}>{fmt$(rev26)}</div>
                        <div style={deltaCell(revDiff)}>
                          {deltaText(revDiff, revPct)}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>{fmt$(rev25)}</div>

                        {/* AOV row */}
                        {(ord25 > 0 || ord26 > 0) && (
                          <>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
                              {ord26 > 0 && <>AOV: <strong style={{ color: 'var(--orange)' }}>{fmt$(aov26)}</strong></>}
                            </div>
                            <div></div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
                              {ord25 > 0 && <>AOV: <strong style={{ color: 'var(--navy)' }}>{fmt$(aov25)}</strong></>}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Chart */}
                    <div style={{ padding: '8px 4px 4px' }}>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={yoyChartData} barGap={2} barSize={16}>
                          <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={28} />
                          <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'Montserrat' }} />
                          <Bar dataKey="orders2026" fill={ORANGE} name="2026" radius={[2,2,0,0]} />
                          <Bar dataKey="orders2025" fill={NAVY}   name="2025" radius={[2,2,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* MAP VIEW */}
          {yoyView === 'map' && (
            <div style={{ padding: '12px 16px', position: 'relative', userSelect: 'none' }}>
              {mapLoading && <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center', padding: 16 }}>Loading map…</div>}
              {!mapLoading && mapData && (() => {
                const rows = mapData?.stateBreakdowns ?? (Array.isArray(mapData) ? mapData : mapData?.data ?? []);
                const ordersByState = {};
                rows.forEach(r => { ordersByState[r.state] = Number(r.ordersCount ?? r.orderCount ?? 0); });
                const maxOrders = Math.max(1, ...Object.values(ordersByState));
                const totalOrders = rows.reduce((s, r) => s + Number(r.ordersCount ?? r.orderCount ?? 0), 0);
                return (
                  <>
                    <ComposableMap projection="geoAlbersUsa" style={{ width: `${mapScale}%`, height: 'auto', display: 'block', margin: '0 auto' }}>
                      <Geographies geography={GEO_URL}>
                        {({ geographies }) => geographies.map(geo => {
                          const name = geo.properties?.name;
                          const abbr = NAME_TO_ABBR[name];
                          const orders = abbr ? (ordersByState[abbr] ?? 0) : (ordersByState[name] ?? 0);
                          const color = heatColor(orders / maxOrders);
                          return (
                            <Geography key={geo.rsmKey} geography={geo}
                              style={{ default: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' }, hover: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none', opacity: 0.8 }, pressed: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' } }}
                              onMouseEnter={e => setMapTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr: abbr ?? name, orders })}
                              onMouseLeave={() => setMapTooltip(null)}
                            />
                          );
                        })}
                      </Geographies>
                      {showDots && Object.entries(ordersByState).map(([abbr, orders]) => {
                        const coords = STATE_CENTROIDS[abbr];
                        if (!coords || !orders) return null;
                        const r = MAX_DOT_R * Math.sqrt(orders / maxOrders);
                        return (
                          <Marker key={abbr} coordinates={coords}>
                            <circle r={r} fill="var(--navy)" fillOpacity={0.65} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }}
                              onMouseEnter={e => setMapTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr, orders })}
                              onMouseLeave={() => setMapTooltip(null)}
                            />
                            {r >= 10 && (
                              <text textAnchor="middle" dominantBaseline="middle" style={{ fontSize: Math.max(7, Math.min(11, r * 0.6)), fontWeight: 700, fill: '#fff', pointerEvents: 'none', fontFamily: 'Montserrat, sans-serif' }}>
                                {orders >= 1000 ? `${(orders / 1000).toFixed(1)}k` : orders}
                              </text>
                            )}
                          </Marker>
                        );
                      })}
                    </ComposableMap>
                    {mapTooltip && (
                      <div style={{ position: 'absolute', left: mapTooltip.x + 12, top: mapTooltip.y - 10, background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10 }}>
                        {mapTooltip.abbr}: {mapTooltip.orders.toLocaleString()} orders
                        {totalOrders > 0 && <span style={{ fontWeight: 400, opacity: 0.8 }}> ({(mapTooltip.orders / totalOrders * 100).toFixed(1)}%)</span>}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>0</span>
                      <div style={{ width: 100, height: 10, borderRadius: 4, background: 'linear-gradient(to right, #f5e8d8, #DC582A)' }} />
                      <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{maxOrders.toLocaleString()}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* ── YTD KPIs ── */}
        {!kpiData && !kpiError && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="card" style={{ padding: '12px 14px' }}>
                <div style={{ marginBottom: 4 }}><span className="skel" style={{ width: 100, height: 9 }} /></div>
                <div><span className="skel" style={{ width: 90, height: 22 }} /></div>
                <div style={{ marginTop: 4 }}><span className="skel" style={{ width: 110, height: 12 }} /></div>
              </div>
            ))}
          </div>
        )}
        {kpiError && <div style={{ padding: '8px 0', fontSize: 11, color: 'var(--red)' }}>⚠ KPI load failed: {kpiError}</div>}
        {kpiData && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {[
              { label: `Sales (${new Date().getMonth()+1}/1 - ${new Date().getMonth()+1}/${new Date().getDate()})`, value: kpiData.sales.value, sub: kpiData.sales.compare, trend: kpiData.sales.trend },
              { label: `Profit (${new Date().getMonth()+1}/1 - ${new Date().getMonth()+1}/${new Date().getDate()})`, value: kpiData.profit.value, sub: `Margin ${kpiData.profit.margin}`, trend: kpiData.profit.trend },
              { label: `Orders (${new Date().getMonth()+1}/1 - ${new Date().getMonth()+1}/${new Date().getDate()})`, value: kpiData.orders.value, sub: kpiData.orders.compare, trend: kpiData.orders.trend },
              { label: `Avg Profit/Box (${new Date().getMonth()+1}/1 - ${new Date().getMonth()+1}/${new Date().getDate()})`, value: kpiData.avgProfit.value, sub: kpiData.avgProfit.compare, trend: kpiData.avgProfit.trend },
            ].map(({ label, value, sub, trend }) => (
              <div key={label} className="card" style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: trend === 'up' ? 'var(--green)' : trend === 'down' ? 'var(--red)' : 'var(--gray-500)', marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── GA4 YOY (Google Analytics) ── */}
        {(yoyView === 'today' || yoyView === 'week') && (() => {
          const fmtNumber = (n) => Number(n || 0).toLocaleString();
          const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
          const pctDelta = (cur, prior) => {
            const c = Number(cur || 0), p = Number(prior || 0);
            if (p === 0) return c > 0 ? '+∞' : '';
            return `${c >= p ? '▲' : '▼'} ${Math.abs((c - p) / p * 100).toFixed(1)}%`;
          };
          const deltaColor = (cur, prior) => Number(cur || 0) >= Number(prior || 0) ? 'var(--green)' : 'var(--red)';
          const periodLabel = yoyView === 'today' ? 'Today' : 'Last 7 Days';
          const tiles = ga4Data ? [
            { key: 'sessions', label: 'Sessions', cur: ga4Data.current.sessions, prior: ga4Data.prior.sessions, fmt: fmtNumber },
            { key: 'activeUsers', label: 'Users', cur: ga4Data.current.activeUsers, prior: ga4Data.prior.activeUsers, fmt: fmtNumber },
            { key: 'transactions', label: 'Purchases', cur: ga4Data.current.transactions, prior: ga4Data.prior.transactions, fmt: fmtNumber },
            { key: 'purchaseRevenue', label: 'Revenue', cur: ga4Data.current.purchaseRevenue, prior: ga4Data.prior.purchaseRevenue, fmt: fmtMoney },
          ] : [];
          // Format prior date(s) for the header. Parse YMD into a local Date
          // (avoids the UTC midnight → previous-day drift you'd get from new Date("YYYY-MM-DD")).
          const parseYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
          const fmtDow = (d) => d.toLocaleDateString('en-US', { weekday: 'short' });
          const fmtMd = (d) => d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
          const fmtMdy = (d) => d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
          let comparisonLabel = 'vs same period last year';
          if (ga4Data?.ranges) {
            const ps = parseYmd(ga4Data.ranges.priorStart);
            const pe = parseYmd(ga4Data.ranges.priorEnd);
            comparisonLabel = yoyView === 'today'
              ? `vs ${fmtDow(ps)} ${fmtMdy(ps)}`
              : `vs ${fmtMd(ps)} – ${fmtMdy(pe)}`;
          }
          const drillTile = ga4DrillMetric ? tiles.find(t => t.key === ga4DrillMetric) : null;
          const drillCacheKey = ga4DrillMetric ? `${yoyView}:${ga4DrillMetric}` : null;
          const drillBody = drillCacheKey ? ga4DrillCache[drillCacheKey] : null;
          return (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span>📊 Google Analytics — {periodLabel}</span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, whiteSpace: 'nowrap' }}>{comparisonLabel}</span>
              </div>
              {ga4Error && (
                <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--gray-500)' }}>
                  {ga4Error === 'GA4 not configured'
                    ? '⚙️ GA4 not configured — set GA4_PROPERTY_ID, GA4_SA_CLIENT_EMAIL, and GA4_SA_PRIVATE_KEY in Netlify env.'
                    : `⚠ ${ga4Error}`}
                </div>
              )}
              {!ga4Data && !ga4Error && (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>
              )}
              {ga4Data && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 12 }}>
                  {tiles.map(t => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setGa4DrillMetric(t.key)}
                      style={{
                        textAlign: 'left', font: 'inherit', cursor: 'pointer',
                        padding: '10px 12px', background: 'var(--gray-50, #f7f8fa)',
                        border: '1px solid transparent', borderRadius: 8,
                        WebkitTapHighlightColor: 'rgba(0,59,92,0.08)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--orange)' }}>BY SOURCE ›</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)', lineHeight: 1 }}>{t.fmt(t.cur)}</div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: deltaColor(t.cur, t.prior) }}>
                            {pctDelta(t.cur, t.prior)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                            prior: {t.fmt(t.prior)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {ga4DrillMetric && (
                <div
                  onClick={() => setGa4DrillMetric(null)}
                  style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                    zIndex: 1000, display: 'flex', alignItems: 'flex-end',
                    animation: 'ga4DrillFadeIn 180ms ease-out',
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%', background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
                      maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
                      animation: 'ga4DrillSlideUp 220ms ease-out',
                      paddingBottom: 'env(safe-area-inset-bottom, 0)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
                      <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--gray-300, #d4d8de)' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 16px 8px', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {drillTile?.label} by source — {periodLabel}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 600, marginTop: 2 }}>{comparisonLabel}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setGa4DrillMetric(null)}
                        aria-label="Close"
                        style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, color: 'var(--gray-500)', cursor: 'pointer', padding: 4 }}
                      >×</button>
                    </div>
                    {ga4DrillLoading && (
                      <div style={{ padding: '24px 16px', fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>Loading…</div>
                    )}
                    {ga4DrillError && (
                      <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--red)' }}>⚠ {ga4DrillError}</div>
                    )}
                    {drillBody && drillTile && (() => {
                      const fmt = drillTile.fmt;
                      const totalCur = drillBody.channels.reduce((s, c) => s + c.current, 0);
                      const rows = drillBody.channels.filter(c => c.current > 0 || c.prior > 0);
                      if (rows.length === 0) {
                        return <div style={{ padding: '24px 16px', fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>No data for this period.</div>;
                      }
                      return (
                        <div style={{ padding: '4px 16px 16px' }}>
                          {rows.map((c) => {
                            const share = totalCur > 0 ? c.current / totalCur * 100 : 0;
                            return (
                              <div key={c.channel} style={{ padding: '10px 0', borderBottom: '1px solid var(--gray-200, #e6e8ec)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{c.channel || '(unknown)'}</div>
                                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--navy)' }}>{fmt(c.current)}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 3 }}>
                                  <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                                    {share.toFixed(1)}% of total · prior: {fmt(c.prior)}
                                  </div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: deltaColor(c.current, c.prior) }}>
                                    {pctDelta(c.current, c.prior)}
                                  </div>
                                </div>
                                <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: 'var(--gray-100, #f0f1f3)' }}>
                                  <div style={{ height: 3, borderRadius: 2, background: 'var(--orange)', width: `${Math.min(100, share)}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── KEY ALERTS (collapsible) ── */}
        {(() => {
          const activeCount = keyAlerts.filter(a => Number(a?.value) > 0).length;
          return (
            <div className="card" style={{ marginBottom: 12 }}>
              <div
                className="card-header"
                onClick={() => setAlertsExpanded(v => !v)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
              >
                <span>⚠ Alerts{activeCount > 0 ? ` (${activeCount} active)` : ''}</span>
                <span style={{ fontSize: 12, color: 'var(--gray-500)', transform: alertsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>▾</span>
              </div>
              {alertsExpanded && (
                <>
                  {!alerts && !alertsError && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
                  {alertsError && <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--red)' }}>⚠ {alertsError}</div>}
                  {keyAlerts.map((a, i) => (
                    <div key={i} className="alert-item" onClick={() => alertPageMap[a.label] && navigateTo(alertPageMap[a.label])}>
                      <span className="alert-label">{a.label}</span>
                      <span className={`alert-count ${a.type}`}>
                        {a.value}
                        {a.reships > 0 && <sup style={{ fontSize: '0.55em', fontWeight: 600, color: 'var(--gray-500)', marginLeft: 1 }}>+{a.reships}</sup>}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })()}

        {/* ── VIP + DELIVERY RECON (left, stacked) + FEEDBACK (right, full-height) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto auto', gap: 8, marginBottom: 12 }}>
          {/* VIP — top-left */}
          <div className="card" style={{ padding: '10px 12px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>👑 VIP Members</div>
            {vipError && <div style={{ fontSize: 10, color: 'var(--red)' }}>⚠ {vipError}</div>}
            {vipData ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', lineHeight: 1 }}>{vipData.current.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700, marginTop: 4 }}>{vipPct}% of {vipData.goal2026.toLocaleString()} goal</div>
                <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'var(--gray-200)' }}>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--orange)', width: `${Math.min(100, vipPct)}%` }} />
                </div>
              </>
            ) : <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Loading...</div>}
          </div>

          {/* Shipping Today — right, spans both rows. Moved here from the hero when the Feedback
              tile took its place (Greg, Oct 20, 2026: "bury it down the page"). */}
          <div className="card" style={{ padding: '12px 14px', gridRow: '1 / span 2', cursor: 'pointer' }} onClick={() => navigateTo('shipping-today')}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>📦 Shipping Today</div>
            {alertsError && <div style={{ fontSize: 10, color: 'var(--red)' }}>⚠ {alertsError}</div>}
            {shippingToday ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', lineHeight: 1 }}>{shippingToday.value}</div>
                {shippingToday.sub?.[0] && <div style={{ fontSize: 13, color: 'var(--gray-700)', marginTop: 6 }}>{shippingToday.sub[0].value}</div>}
                {shippingToday.sub?.[1] && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{shippingToday.sub[1].label}: {shippingToday.sub[1].value}</div>}
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--orange)', marginTop: 8 }}>Open Shipping Today ›</div>
              </>
            ) : !alertsError && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Loading...</div>}
          </div>

          {/* Delivery Recon — bottom-left */}
          {deliveryRecon ? (
            <div className="card" style={{ padding: '10px 12px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>🚚 Delivery</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {[
                  { label: 'On Time', value: deliveryRecon.onTime, color: 'var(--green)' },
                  { label: 'Late', value: deliveryRecon.late, color: 'var(--red)' },
                  { label: 'Trouble', value: deliveryRecon.inTrouble, color: 'var(--yellow)' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: 'var(--gray-500)', fontWeight: 700, marginBottom: 2, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: '10px 12px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>🚚 Delivery</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Loading...</div>
            </div>
          )}
        </div>
        {feedbackSheet && (
          <FeedbackSheet feedbackData={feedbackData} extras={feedbackCounts} initialView={feedbackSheet} onClose={() => setFeedbackSheet(null)} />
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }} />
      <OrderAlertsStrip navigateTo={navigateTo} />
      <div className="dashboard-grid">
        {/* LEFT: ALERTS */}
        <div>
          <div className="card">
            <div className="card-header">⚠ Alerts</div>
            {alertsError && <ApiError label="Alerts" error={alertsError} />}
            {!alerts && !alertsError && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {alerts && alerts.map((alert, i) => (
              <div key={i} className="alert-item" onClick={() => alertPageMap[alert.label] && navigateTo(alertPageMap[alert.label])}>
                <div className="alert-label">
                  <span>{alert.label}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className={`alert-count ${alert.type}`}>
                  {alert.value}
                  {alert.reships > 0 && <sup style={{ fontSize: '0.55em', fontWeight: 600, color: 'var(--gray-500)', marginLeft: 1 }}>+{alert.reships}</sup>}
                </span>
                  {alert.sub && (
                    <div style={{ marginTop: 2 }}>
                      {alert.sub.map((s, j) => (
                        <div key={j} style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 600 }}>{s.label}: {s.value}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* DELIVERY RECONCILIATION */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">Delivery Reconciliations</div>
            {reconError && <ApiError label="Delivery Recon" error={reconError} />}
            {!deliveryRecon && !reconError && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {deliveryRecon && <>
              <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600 }}>
                Duration: {deliveryRecon.period}
              </div>
              <div className="recon-row"><span>On Time</span><span className="recon-value good">{deliveryRecon.onTime}</span></div>
              <div className="recon-row"><span>Late</span><span className="recon-value danger">{deliveryRecon.late}</span></div>
              <div className="recon-row"><span>In Trouble</span><span className="recon-value warning">{deliveryRecon.inTrouble}</span></div>
              <div className="recon-row"><span>Late Order Total</span><span className="recon-value danger">{deliveryRecon.lateOrderTotal}</span></div>
              <div className="recon-row"><span>Average late time</span><span className="recon-value">{deliveryRecon.avgLateTime}</span></div>
            </>}
          </div>
        </div>

        {/* CENTER: KPIs + CHARTS */}
        <div>
          {/* KPI CARDS */}
          {kpiError && <ApiError label="KPIs" error={kpiError} />}
          {!kpiData && !kpiError && <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--gray-500)' }}>Loading KPIs...</div>}
          {(kpiData || vipBoard || vipBoardError) && (() => {
            const now = new Date();
            const mtdRange = `${now.getMonth()+1}/1 - ${now.getMonth()+1}/${now.getDate()}`;
            return (
            <div className="kpi-grid">
              {kpiData && <>
              <div className="kpi-card">
                <div className="kpi-label">Sales ({mtdRange})</div>
                <div className="kpi-value">{kpiData.sales.value}</div>
                <div className={`kpi-compare ${kpiData.sales.trend}`}>vs {kpiData.sales.compare}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Profit ({mtdRange})</div>
                <div className="kpi-value">{kpiData.profit.value}</div>
                <div className="kpi-compare" style={{ color: 'var(--gray-500)' }}>Current Margin: <strong>{kpiData.profit.margin}</strong></div>
                <div className="kpi-compare" style={{ color: 'var(--gray-500)' }}>Last Year: {kpiData.profit.lastYearMargin}</div>
              </div>
              </>}

              {/* The VIP block from the condo TV board, 7-day trend and all (Greg's
                  ask, 2026-08-27). It takes the left half of the bottom row, which
                  Orders and Avg Profit Per Box gave up by sharing the tile beside it. */}
              <VipBoardCard vip={vipBoard} error={vipBoardError} />

              {/* Feedback on top, Orders | Avg Profit Per Box side by side under it, one
                  tile (Greg, Oct 20, 2026). Feedback used to sit below the fold at the
                  bottom of the right column; the white space between Orders and Avg
                  Profit was the room to bring it up. */}
              {(kpiData || feedbackData || feedbackError) && (
              <div className="kpi-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div className="kpi-label">⭐ Feedback</div>
                    {feedbackData && (
                      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                        {/* The review count, big and bold: Greg could never find it at 11px grey (Oct 21, 2026). */}
                        <b style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{Number(feedbackData.count || 0).toLocaleString()} reviews</b>
                        {feedbackRate != null && <> · <b style={{ color: 'var(--orange)' }}>{feedbackRate}%</b> engagement ({feedbackRequests.toLocaleString()} sent)</>}
                        {' · '}{feedbackYmd ? `sent ${sendLabel(feedbackYmd)}` : feedbackData.date}
                      </span>
                    )}
                  </div>
                  {feedbackError && !feedbackData && <ApiError label="Feedback" error={feedbackError} />}
                  {!feedbackData && !feedbackError && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
                  {feedbackData && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '4px 16px', marginTop: 6 }}>
                      {feedbackData.scores.map((s, i) => (
                        <div key={i}
                          onClick={s.label === 'Product' ? () => setFeedbackSheet('category') : undefined}
                          title={s.label === 'Product' ? 'See the product score by category' : undefined}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, padding: '3px 0', borderBottom: '1px solid var(--gray-100)', cursor: s.label === 'Product' ? 'pointer' : 'default' }}>
                          <span style={{ color: 'var(--gray-600)' }}>
                            {s.label}{s.label === 'Product' && <span style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 700, marginLeft: 4 }}>›</span>}
                          </span>
                          <span><span className="feedback-star">★</span><span className="feedback-score" style={s.label === 'Overall' ? { fontSize: 16 } : undefined}>{s.score}</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                  {feedbackData && feedbackCounts && (
                    <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 12 }}>
                      <a onClick={() => setFeedbackSheet('photos')} style={{ cursor: 'pointer', color: 'var(--orange)', fontWeight: 600 }}>
                        <b>{feedbackCounts.photos}</b> photo {feedbackCounts.photos === 1 ? 'entry' : 'entries'}
                      </a>
                      <a onClick={() => setFeedbackSheet('reviews')} style={{ cursor: 'pointer', color: 'var(--orange)', fontWeight: 600 }}>
                        <b>{feedbackCounts.review_sites}</b> sent to review sites
                        {feedbackCounts.review_sites_avg != null && (
                          <span title="Average Overall score of the customers sent to review sites" style={{ marginLeft: 6, color: 'var(--navy)' }}>
                            <span className="feedback-star">★</span><b>{feedbackCounts.review_sites_avg.toFixed(2)}</b> avg
                          </span>
                        )}
                      </a>
                    </div>
                  )}
                </div>
                {kpiData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 12, marginTop: 'auto' }}>
                  <div>
                    <div className="kpi-label">Orders ({mtdRange})</div>
                    <div className="kpi-value">{kpiData.orders.value}</div>
                    <div className={`kpi-compare ${kpiData.orders.trend}`}>vs {kpiData.orders.compare}</div>
                  </div>
                  <div>
                    <div className="kpi-label">Avg Profit Per Box ({mtdRange})</div>
                    <div className="kpi-value">{kpiData.avgProfit.value}</div>
                    <div className={`kpi-compare ${kpiData.avgProfit.trend}`}>vs {kpiData.avgProfit.compare}</div>
                  </div>
                </div>
                )}
              </div>
              )}
            </div>
            );
          })()}

          {/* YOY ORDERS CHART */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {desktopYoyPeriod === 'week' && (
                  <button onClick={() => setDesktopWeeksBack(w => w + 1)} style={{ fontSize: 14, width: 24, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1 }}>‹</button>
                )}
                <span>
                  Year-Over-Year Orders Placed
                  {desktopYoyPeriod === 'week' && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--gray-500)' }}> ({getWeekLabel(desktopWeeksBack)})</span>}
                </span>
                {desktopYoyPeriod === 'week' && (
                  <button onClick={() => setDesktopWeeksBack(w => Math.max(-4, w - 1))} style={{ fontSize: 14, width: 24, height: 24, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', lineHeight: 1, opacity: desktopWeeksBack <= -4 ? 0.3 : 1 }} disabled={desktopWeeksBack <= -4}>›</button>
                )}
                <button onClick={() => setDesktopYoyPeriod(v => v === 'week' ? 'today' : 'week')} style={{ fontSize: 10, padding: '2px 9px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)', marginLeft: 4 }}>
                  {desktopYoyPeriod === 'week' ? 'Show Today' : 'Show Weekly'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {desktopShowMap && (
                  <>
                    {[2026, 2025].map(yr => (
                      <button key={yr} onClick={() => setMapYear(yr)} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: mapYear === yr ? getYearColor(yr) : 'var(--gray-200)', color: mapYear === yr ? '#fff' : 'var(--gray-600)' }}>{yr}</button>
                    ))}
                    <button onClick={() => setShowDots(d => !d)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: showDots ? 'var(--navy)' : 'var(--gray-200)', color: showDots ? '#fff' : 'var(--gray-600)' }}>⬤ Dots</button>
                    <button onClick={() => setMapScale(s => Math.max(40, s - 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>−</button>
                    <button onClick={() => setMapScale(s => Math.min(150, s + 10))} style={{ fontSize: 12, width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>+</button>
                  </>
                )}
                <button onClick={() => setDesktopShowMap(m => !m)} style={{ fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: desktopShowMap ? 'var(--orange)' : 'var(--gray-200)', color: desktopShowMap ? '#fff' : 'var(--gray-600)' }}>
                  🗺 {desktopShowMap ? 'Hide Map' : 'Heat Map'}
                </button>
              </div>
            </div>
            <div style={{ padding: '12px 16px' }}>
              {weeklyError && <ApiError label="Weekly Orders" error={weeklyError} />}
              {(desktopHistLoading || (!weeklyOrdersData && !weeklyError) || (desktopYoyPeriod === 'today' && !alerts)) && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
              {weeklyOrdersData && !desktopShowMap && !desktopHistLoading && (desktopYoyPeriod !== 'today' || alerts) && (() => {
                const chartData = desktopWeeksBack !== 0 ? desktopHistData : weeklyOrdersData;
                if (!chartData) return null;

                // Today view
                if (desktopYoyPeriod === 'today') {
                  const td = weeklyOrdersData[new Date().getDay()];
                  if (!td) return null;
                  // Use placed-today count from alerts (matches GetDashboardPlacedAndOnHoldInfo)
                  // Weekly data groups by ship date, not placed date — can differ from placed count
                  const placedTodayAlert = alerts?.find(a => a.label === 'Orders Placed Today');
                  const today2026 = placedTodayAlert?.value ?? td.orders2026;
                  const rev26Num = parseFloat((td.rev2026 ?? '').replace(/[$,]/g, '')) || 0;
                  const rev25Num = parseFloat((td.rev2025 ?? '').replace(/[$,]/g, '')) || 0;
                  const aov26 = today2026 > 0 ? `$${(rev26Num / today2026).toFixed(2)}` : null;
                  const aov25 = td.orders2025 > 0 ? `$${(rev25Num / td.orders2025).toFixed(2)}` : null;
                  const pct = td.orders2025 > 0 ? ((today2026 - td.orders2025) / td.orders2025 * 100) : null;
                  const up = today2026 >= td.orders2025;
                  const now = new Date();
                  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  return (
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 12 }}>{M[now.getMonth()]} {now.getDate()}, {now.getFullYear()}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 12 }}>
                        {[{yr:'2026',orders:today2026,rev:td.rev2026,aov:aov26,color:ORANGE},{yr:'2025',orders:td.orders2025,rev:td.rev2025,aov:aov25,color:NAVY}].map(({yr,orders,rev,aov,color}) => (
                          <div key={yr}>
                            <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{yr}</div>
                            <div style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{orders}</div>
                            <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4 }}>{rev}</div>
                            {aov && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>AOV: {aov}</div>}
                          </div>
                        ))}
                      </div>
                      {pct !== null && <div style={{ fontSize: 13, fontWeight: 700, color: up ? 'var(--green)' : 'var(--red)', marginBottom: 12 }}>{up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}% vs same day last year</div>}
                      <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={[{ ...td, orders2026: today2026, date: `${M[now.getMonth()]} ${now.getDate()}` }]} barGap={8} barSize={60}>
                          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip content={<YoyTooltip />} />
                          <Bar dataKey="orders2025" fill={NAVY}   name="2025" radius={[2,2,0,0]} label={barValueLabel} />
                          <Bar dataKey="orders2026" fill={ORANGE} name="2026" radius={[2,2,0,0]} label={barValueLabel} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                }

                // Week view — compute actual dates for tooltip
                const now = new Date();
                const daysToSat = now.getDay() === 6 ? 0 : 6 - now.getDay();
                const weekSat = new Date(now); weekSat.setDate(now.getDate() + daysToSat - desktopWeeksBack * 7);
                const weekSun = new Date(weekSat); weekSun.setDate(weekSat.getDate() - 6);
                const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const chartDataWithDates = chartData.map((d, i) => {
                  const row = { ...d };
                  const dayDate = new Date(weekSun); dayDate.setDate(weekSun.getDate() + i);
                  row.date = `${M[dayDate.getMonth()]} ${dayDate.getDate()}`;
                  selectedYears.forEach(yr => { row[`orders${yr}`] = extraYearData[yr]?.[i]?.orders ?? 0; });
                  return row;
                });

                return (<>
                {/* Year toggles */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginRight: 8 }}>
                    {[...[...EXTRA_YEARS].filter(yr => selectedYears.has(yr)).sort((a,b) => a-b), 2025, 2026].map(yr => (
                      <span key={yr} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: getYearColor(yr) }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: getYearColor(yr), display: 'inline-block' }} />{yr}
                      </span>
                    ))}
                  </div>
                  <div style={{ width: 1, height: 16, background: 'var(--gray-200)' }} />
                  {/* Extra year toggles */}
                  {EXTRA_YEARS.map(yr => {
                    const on = selectedYears.has(yr);
                    const loading = loadingYears.has(yr);
                    return (
                      <button key={yr} onClick={() => {
                        if (on) {
                          setSelectedYears(prev => { const s = new Set(prev); s.delete(yr); return s; });
                        } else {
                          setSelectedYears(prev => new Set(prev).add(yr));
                          if (!extraYearData[yr]) {
                            setLoadingYears(prev => new Set(prev).add(yr));
                            fetchWeeklyOrdersForYear(yr)
                              .then(data => setExtraYearData(prev => ({ ...prev, [yr]: data })))
                              .finally(() => setLoadingYears(prev => { const s = new Set(prev); s.delete(yr); return s; }));
                          }
                        }
                      }} style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                        cursor: 'pointer',
                        border: `2px solid ${getYearColor(yr)}`,
                        background: on ? getYearColor(yr) : '#fff',
                        color: on ? '#fff' : getYearColor(yr),
                        opacity: loading ? 0.5 : 1,
                        fontFamily: 'Montserrat',
                      }}>{loading ? '…' : yr}</button>
                    );
                  })}
                </div>

                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartDataWithDates} barGap={2}>
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<YoyTooltip />} />
                    {[...EXTRA_YEARS].filter(yr => selectedYears.has(yr)).sort((a, b) => a - b).map(yr => (
                      <Bar key={yr} dataKey={`orders${yr}`} fill={getYearColor(yr)} name={String(yr)} radius={[2,2,0,0]} label={barValueLabel} />
                    ))}
                    <Bar dataKey="orders2025" fill={NAVY}   name="2025" radius={[2,2,0,0]} label={barValueLabel} />
                    <Bar dataKey="orders2026" fill={ORANGE} name="2026" radius={[2,2,0,0]} label={barValueLabel} />
                  </BarChart>
                </ResponsiveContainer>

                <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr>
                      <td style={{ color: 'var(--gray-500)', fontWeight: 700, paddingBottom: 4 }}></td>
                      {chartDataWithDates.map(d => (
                        <td key={d.day} style={{ textAlign: 'center', fontWeight: 700, color: 'var(--navy)', paddingBottom: 4 }}>{d.day}<br/><span style={{fontWeight:400,fontSize:9,color:'var(--gray-400)'}}>{d.date}</span></td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...EXTRA_YEARS].filter(yr => selectedYears.has(yr) && extraYearData[yr]).sort((a, b) => a - b).map(yr => (
                      <React.Fragment key={yr}>
                        <tr style={{ color: getYearColor(yr), fontWeight: 700 }}>
                          <td style={{ fontWeight: 600 }}>{yr} Orders</td>
                          {extraYearData[yr].map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.orders}</td>)}
                        </tr>
                        <tr style={{ color: getYearColor(yr), opacity: 0.75 }}>
                          <td style={{ fontWeight: 600 }}>{yr} Rev</td>
                          {extraYearData[yr].map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.rev}</td>)}
                        </tr>
                      </React.Fragment>
                    ))}
                    <tr style={{ color: NAVY }}>
                      <td style={{ paddingRight: 8, fontWeight: 600 }}>2025 Orders</td>
                      {chartDataWithDates.map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.orders2025}</td>)}
                    </tr>
                    <tr style={{ color: NAVY, opacity: 0.75 }}>
                      <td style={{ fontWeight: 600 }}>2025 Rev</td>
                      {chartDataWithDates.map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.rev2025}</td>)}
                    </tr>
                    <tr style={{ color: ORANGE, fontWeight: 700 }}>
                      <td style={{ fontWeight: 600 }}>2026 Orders</td>
                      {chartDataWithDates.map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.orders2026}</td>)}
                    </tr>
                    <tr style={{ color: ORANGE, opacity: 0.75 }}>
                      <td style={{ fontWeight: 600 }}>2026 Rev</td>
                      {chartDataWithDates.map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.rev2026}</td>)}
                    </tr>
                    <tr style={{ color: 'var(--orange)' }}>
                      <td style={{ fontWeight: 600 }}>Avg Profit</td>
                      {chartDataWithDates.map(d => <td key={d.day} style={{ textAlign: 'center' }}>{d.profit2026}</td>)}
                    </tr>
                  </tbody>
                </table>
              </>); })()}
              {desktopShowMap && (() => {
                if (mapLoading) return <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '16px 0', textAlign: 'center' }}>Loading map…</div>;
                if (!mapData) return null;
                const rows = mapData?.stateBreakdowns ?? (Array.isArray(mapData) ? mapData : mapData?.data ?? []);
                const ordersByState = {};
                rows.forEach(r => { ordersByState[r.state] = Number(r.ordersCount ?? r.orderCount ?? 0); });
                const maxOrders = Math.max(1, ...Object.values(ordersByState));
                const totalOrders = rows.reduce((s, r) => s + Number(r.ordersCount ?? r.orderCount ?? 0), 0);
                return (
                  <div style={{ position: 'relative', userSelect: 'none' }}>
                    <ComposableMap projection="geoAlbersUsa" style={{ width: `${mapScale}%`, height: 'auto', display: 'block', margin: '0 auto' }}>
                      <Geographies geography={GEO_URL}>
                        {({ geographies }) => geographies.map(geo => {
                          const name = geo.properties?.name;
                          const abbr = NAME_TO_ABBR[name];
                          const orders = abbr ? (ordersByState[abbr] ?? 0) : (ordersByState[name] ?? 0);
                          const color = heatColor(orders / maxOrders);
                          return (
                            <Geography key={geo.rsmKey} geography={geo}
                              style={{ default: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' }, hover: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none', opacity: 0.8 }, pressed: { fill: color, stroke: '#fff', strokeWidth: 0.5, outline: 'none' } }}
                              onMouseEnter={e => setMapTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr: abbr ?? name, orders })}
                              onMouseLeave={() => setMapTooltip(null)}
                            />
                          );
                        })}
                      </Geographies>
                      {showDots && Object.entries(ordersByState).map(([abbr, orders]) => {
                        const coords = STATE_CENTROIDS[abbr];
                        if (!coords || !orders) return null;
                        const r = MAX_DOT_R * Math.sqrt(orders / maxOrders);
                        return (
                          <Marker key={abbr} coordinates={coords}>
                            <circle r={r} fill="var(--navy)" fillOpacity={0.65} stroke="#fff" strokeWidth={1} style={{ cursor: 'pointer' }}
                              onMouseEnter={e => setMapTooltip({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, abbr, orders })}
                              onMouseLeave={() => setMapTooltip(null)}
                            />
                            {r >= 10 && (
                              <text textAnchor="middle" dominantBaseline="middle" style={{ fontSize: Math.max(7, Math.min(11, r * 0.6)), fontWeight: 700, fill: '#fff', pointerEvents: 'none', fontFamily: 'Montserrat, sans-serif' }}>
                                {orders >= 1000 ? `${(orders / 1000).toFixed(1)}k` : orders}
                              </text>
                            )}
                          </Marker>
                        );
                      })}
                    </ComposableMap>
                    {mapTooltip && (
                      <div style={{ position: 'absolute', left: mapTooltip.x + 12, top: mapTooltip.y - 10, background: 'var(--navy)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10 }}>
                        {mapTooltip.abbr}: {mapTooltip.orders.toLocaleString()} orders
                        {totalOrders > 0 && <span style={{ fontWeight: 400, opacity: 0.8 }}> ({(mapTooltip.orders / totalOrders * 100).toFixed(1)}%)</span>}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>0</span>
                      <div style={{ width: 120, height: 10, borderRadius: 4, background: 'linear-gradient(to right, #f5e8d8, #DC582A)' }} />
                      <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{maxOrders.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* YOY CATEGORY CHART */}
          <div className="card">
            <div className="card-header">Year-Over-Year Sales by Category</div>
            <div style={{ padding: '12px 16px' }}>
              {categoryError && <ApiError label="Category Data" error={categoryError} />}
              {!categoryData && !categoryError && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
              {categoryData && <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={categoryData} barGap={2}>
                    <XAxis dataKey="name" tick={{ fontSize: 9.5 }} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'Montserrat' }} />
                    <Bar dataKey="orders2025" fill="#00395C" name="2025 Orders" radius={[2,2,0,0]} />
                    <Bar dataKey="orders2026" fill="#DC582A" name="2026 Orders" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px', color: 'var(--gray-500)' }}></th>
                      {categoryData.map(c => <th key={c.name} style={{ padding: '4px 4px', color: 'var(--gray-700)', fontWeight: 700 }}>{c.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '3px 6px', color: 'var(--gray-500)', fontWeight: 600 }}>2025 Orders</td>
                      {categoryData.map(c => <td key={c.name} style={{ textAlign: 'center', padding: '3px 4px' }}>{c.orders2025}</td>)}
                    </tr>
                    <tr>
                      <td style={{ padding: '3px 6px', color: 'var(--gray-500)', fontWeight: 600 }}>2025 Sales</td>
                      {categoryData.map(c => <td key={c.name} style={{ textAlign: 'center', padding: '3px 4px' }}>{c.sales2025}</td>)}
                    </tr>
                    <tr style={{ fontWeight: 700, color: 'var(--navy)' }}>
                      <td style={{ padding: '3px 6px', fontWeight: 600 }}>2026 Orders</td>
                      {categoryData.map(c => <td key={c.name} style={{ textAlign: 'center', padding: '3px 4px' }}>{c.orders2026}</td>)}
                    </tr>
                    <tr style={{ color: 'var(--orange)', fontWeight: 600 }}>
                      <td style={{ padding: '3px 6px', fontWeight: 600 }}>2026 Sales</td>
                      {categoryData.map(c => <td key={c.name} style={{ textAlign: 'center', padding: '3px 4px' }}>{c.sales2026}</td>)}
                    </tr>
                  </tbody>
                </table>
              </>}
            </div>
          </div>
        </div>

        {/* RIGHT: VIP + FEEDBACK */}
        <div>
          {/* VIP CLUB */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>👑 VIP Club</span>
              {vipData && <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>2026 Goal: {vipData.goal2026.toLocaleString()} ✏</span>}
            </div>
            {vipError && <ApiError label="VIP Club" error={vipError} />}
            {!vipData && !vipError && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {vipData && <>
              <div className="vip-goal-ring">
                <div className="vip-ring">
                  <div className="vip-ring-number">{(vipActiveCount ?? vipData.activeMembers).toLocaleString()}</div>
                  <div className="vip-ring-label">Total VIPs</div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--orange)', marginTop: 2 }}>{vipData.renewalAmount}</div>
                </div>
                {/* The day's move, on the desktop the way the phone carries it. Greg,
                    2026-09-02: "it's the same function as for the pwa. i just want it to
                    display in desktop" - so this is vipSignupCount, the SAME value the
                    phone hero tile prints, hoisted to component scope rather than
                    recomputed. Nothing is derived here.
                    It sits UNDER the ring rather than beside the number: the ring is
                    110px with 98px of usable width, and an April day reading "−1,273"
                    overflows it. Blank on a flat day - a zero in the one loud slot on the
                    card reads as a fault, the rule VipBoardCard already follows.
                    ⚠️ CORRECTED 2026-09-17: it is today's SIGNUPS, which is what the phone
                    and the wall now show; the NET move lives in the 7-day bars. The ring above it
                    counts TOTAL members, and the two differ by the day's auto-renew
                    toggles (measured 2026-09-02: renewing +19, total +20, one opt-out).
                    Labelled, and label FIRST: a bare "+19" here reads as the ring having
                    moved by 19, and "renewing +19" reads as nineteen renewals having
                    been processed - which is a real and different figure (renewals_today
                    in the payload, a current member buying another year). */}
                {vipSignupCount && (
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--orange)', marginTop: -2 }}>
                    {vipSignupCount} signed up today
                  </div>
                )}
              </div>


              <div className="vip-section-header">Current Week</div>
              <div className="vip-stat-row"><span className="vip-stat-label">New VIPs</span><span className="vip-stat-value">{vipData.week.newVips}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Reactivated VIPs</span><span className="vip-stat-value">{vipData.week.reactivated}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total VIP Orders</span><span className="vip-stat-value">{vipData.week.totalVipOrders}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total New VIP Revenue</span><span className="vip-stat-value">{vipData.week.newVipRevenue}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total Existing VIP Revenue</span><span className="vip-stat-value">{vipData.week.existingVipRevenue}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total VIP Revenue</span><span className="vip-stat-value orange">{vipData.week.totalVipRevenue}</span></div>

              <div className="vip-section-header">Current Month</div>
              <div className="vip-stat-row"><span className="vip-stat-label">New VIPs</span><span className="vip-stat-value">{vipData.month.newVips}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Reactivated VIPs</span><span className="vip-stat-value">{vipData.month.reactivated}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total VIP Orders</span><span className="vip-stat-value">{vipData.month.totalVipOrders}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total New VIP Revenue</span><span className="vip-stat-value">{vipData.month.newVipRevenue}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total Existing VIP Revenue</span><span className="vip-stat-value">{vipData.month.existingVipRevenue}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total VIP Revenue</span><span className="vip-stat-value orange">{vipData.month.totalVipRevenue}</span></div>

              <div className="vip-section-header" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => setVipPurchasesOpen(!vipPurchasesOpen)}>
                <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: vipPurchasesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                Active VIP Purchases
              </div>
              {vipPurchasesOpen && vipData.activePurchases.map((p, i) => (
                <div key={i} className="vip-stat-row"><span className="vip-stat-label">{p.label}</span><span className="vip-stat-value">{p.value.toLocaleString()}</span></div>
              ))}


              <div className="vip-section-header">Renewals Coming Due</div>
              {(vipData.renewalSchedule || []).map((m, i) => (
                <div key={i} className="vip-stat-row"><span className="vip-stat-label">{m.label}</span><span className="vip-stat-value">{m.members.toLocaleString()} · {m.amount}</span></div>
              ))}
              <div style={{ height: 8 }} />
              <div className="vip-stat-row"><span className="vip-stat-label">● Total VIPs</span><span className="vip-stat-value">{(vipActiveCount ?? vipData.activeMembers).toLocaleString()}</span></div>
              <div className="vip-stat-row"><span className="vip-stat-label">Total Active VIPs</span><span className="vip-stat-value orange">{(vipRenewingCount ?? vipData.activeRenewals).toLocaleString()}</span></div>
              <div className="vip-stat-row" style={{ marginBottom: 12 }}><span className="vip-stat-label">Renewal Amount</span><span className="vip-stat-value orange">{vipData.renewalAmount}</span></div>
            </>}
          </div>

        </div>
      </div>
      {feedbackSheet && (
        <FeedbackSheet feedbackData={feedbackData} extras={feedbackCounts} initialView={feedbackSheet} onClose={() => setFeedbackSheet(null)} />
      )}
    </div>
  );
}
